import { MarketplaceOpportunity } from '../slices/marketplace/opportunity/models/opportunity';
import { PracticeProfileInfo } from '../slices/profiles-data-slice/models/practice-profile-info';
import { PracticeFocus } from './constants/PracticeFocus.enum';

export interface FieldDifference {
    fieldName: string;
    hasDifference: boolean;
}

export interface PracticeDifference {
    practiceId: number;
    practiceName: string;
    fields: FieldDifference[];
}

export type SellerDataDifferences = PracticeDifference[];

const compareProperties = (prop1: any, prop2: any): boolean => {
    const str1 = (prop1 ?? '').toString().toLowerCase();
    const str2 = (prop2 ?? '').toString().toLowerCase();
    return str1 !== str2;
};

export const sellerDataDifferenceTracker = (
    opportunity: MarketplaceOpportunity,
    profilePractices: PracticeProfileInfo[]
): SellerDataDifferences => {
    const differences: SellerDataDifferences = [];
    const engagmentPractices = opportunity.practices;

    engagmentPractices.forEach((engagementPractice) => {
        const profilePractice = profilePractices.find(
            (p) => p.practiceId === engagementPractice.practiceId
        );

        const practiceFields: FieldDifference[] = [];

        if (profilePractice) {
            practiceFields.push({
                fieldName: 'practiceName',
                hasDifference: compareProperties(
                    engagementPractice.practiceName,
                    profilePractice.practiceName
                ),
            });

            practiceFields.push({
                fieldName: 'ownerName',
                hasDifference: compareProperties(
                    engagementPractice.ownerName,
                    profilePractice.ownerFullName
                ),
            });

            practiceFields.push({
                fieldName: 'practiceOwnershipType',
                hasDifference: compareProperties(
                    engagementPractice.practiceOwnershipType,
                    profilePractice.practiceOwnershipType
                ),
            });

            practiceFields.push({
                fieldName: 'fteDvms',
                hasDifference: compareProperties(
                    engagementPractice.fteDvms,
                    profilePractice.fteDvms
                ),
            });

            practiceFields.push({
                fieldName: 'revenueGrowthRate',
                hasDifference: compareProperties(
                    engagementPractice.revenueGrowthRate,
                    profilePractice.revenueGrowthRate
                ),
            });

            practiceFields.push({
                fieldName: 'practiceType',
                hasDifference: compareProperties(
                    engagementPractice.practiceType,
                    profilePractice.practiceFocusType
                ),
            });

            if (
                engagementPractice.practiceType === PracticeFocus.Other ||
                profilePractice.practiceFocusType === PracticeFocus.Other
            ) {
                practiceFields.push({
                    fieldName: 'otherPracticeType',
                    hasDifference: compareProperties(
                        engagementPractice.otherPracticeType,
                        profilePractice.otherPracticeType
                    ),
                });
            }

            const primaryProfileLocation =
                profilePractice?.practiceLocations?.reduce((max, location) =>
                    (location.fteDvms ?? 0) > (max.fteDvms ?? 0)
                        ? location
                        : max
                );

            practiceFields.push({
                fieldName: 'locationType',
                hasDifference: compareProperties(
                    engagementPractice.locationType,
                    primaryProfileLocation?.locationType
                ),
            });
        }

        differences.push({
            practiceId: engagementPractice.practiceId,
            practiceName: engagementPractice.practiceName,
            fields: practiceFields,
        });
    });

    return differences;
};
